import { DISPLAY_DATE_FORMAT } from 'constants/dates';
import { PRIVILEGES } from 'constants/privileges';
import { ChangeEvent, MouseEvent, useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
} from '@mui/material';
import { AccountEntryService } from 'api';
import {
  AccountEntry,
  AccountEntryStatus,
} from 'api/accountEntries/accountEntries.types';
import { StyledButton } from 'components/Buttons';
import { StyledTableCell, StyledTableHeadCell } from 'components/Table';
import { useAuth } from 'contexts/authContext';
import { format } from 'date-fns';
import { InvoiceStatus } from 'pages/invoices/components/filters/filters.types';
import { FetchInvoiceData } from 'pages/invoices/components/InvoiceDetails/InvoiceDetails.types';
import { CorrectiveInvoice, Invoice } from 'pages/invoices/invoices.types';
import { useTranslation } from 'react-i18next';
import { FaExclamation, FaBan, FaShare } from 'react-icons/fa';
import { ImCheckmark } from 'react-icons/im';
import { MdDelete } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { hasPrivilege } from 'utils/authFunctions.utils';
import {
  canEditInvoice,
  canReviewAllocation,
  isAllocationOwner,
  isInvoiceAllocationExported,
  isInvoiceCorrective,
} from 'utils/invoiceFunctions.utils';
import { numberWithCommas } from 'utils/numberFunctions.utils';
import { AddAllocationDialog } from './components/AddAllocationDialog';
import { AllocationDetails } from './components/AllocationDetails';
import { ComplaintAllocationDialog } from './components/ComplaintAllocationDialog';
import { ConfirmAllocationDialog } from './components/ConfirmAllocationDialog';
import { RejectAllocationDialog } from './components/RejectAllocationDialog';
import { WithdrawCorrectionAllocationDialog } from './components/WithdrawCorrectionAllocationDialog';
import {
  allocationsHeadCells,
  allocationStatus,
} from './InvoiceAllocations.const';
import * as S from './InvoiceAllocations.css';
import { AllocationsTableData, AllocationStatus, JobStatus } from './types';
import { isAllocationJobBilledOrCancelled } from './utils';

type Props = {
  invoice: Invoice | CorrectiveInvoice;
  refetchInvoiceData: FetchInvoiceData;
};

function createTableData(
  id: number,
  job: string,
  counterparty: string,
  jobAmount: string,
  creationDate: number,
  status: AccountEntryStatus,
  jobId: number,
  allocationData: AccountEntry,
): AllocationsTableData {
  return {
    id,
    job,
    counterparty,
    jobAmount,
    creationDate: format(creationDate, DISPLAY_DATE_FORMAT),
    status,
    jobId,
    allocationData,
  };
}

export const InvoiceAllocations = ({ invoice, refetchInvoiceData }: Props) => {
  const { user, allowedReplacements, allowedReplacementsByCode } = useAuth();

  const { t } = useTranslation([
    'misc',
    'invoice',
    'counterparty',
    'currency',
    'clause',
  ]);

  const [selectedRows, setSelectedRows] = useState<number[]>([]); // state of checkboxes
  const [actionEntryId, setActionEntryId] = useState(-1); //  id passed to reject/withdraw/complaint dialogs
  const [selectedAllocation, setSelectedAllocation] = useState<
    AccountEntry | undefined
  >(undefined);
  const [entriesToAccept, setEntriesToAccept] = useState<number[]>([]); // ids passed to accept dialog

  const [addAllocationDialogOpen, setAddAllocationDialogOpen] = useState(false);
  const [allocationDetailsDialogOpen, setAllocationDetailsDialogOpen] =
    useState(false);
  const [rejectAllocationDialogOpen, setRejectAllocationDialogOpen] =
    useState(false);
  const [confirmAllocationDialogOpen, setConfirmAllocationDialogOpen] =
    useState(false);
  const [complaintAllocationDialogOpen, setComplaintAllocationDialogOpen] =
    useState(false);
  const [
    withdrawCorrectionAllocationDialogOpen,
    setWithdrawCorrectionAllocationDialogOpen,
  ] = useState(false);

  const navigate = useNavigate();

  const isCorrective = useMemo(
    () => isInvoiceCorrective(invoice.invoiceClass),
    [invoice.invoiceClass],
  );

  const {
    getInvoiceAccountEntry,
    loading: accountEntriesLoading,
    data,
  } = AccountEntryService.useGetInvoiceAccountEntry();

  const rows = useMemo(
    () =>
      data?.map((a) =>
        createTableData(
          a.id,
          a.job.code,
          a.job.customer.counterpartyShortName,
          numberWithCommas(a.invoiceAmount) +
            ' ' +
            invoice.currencyCode.currency,
          a.creationDate,
          a.status,
          a.job.id,
          a,
        ),
      ),
    [data],
  );
  const isRowSelected = (id: number) => selectedRows.indexOf(id) !== -1;
  const canBeAcceptedOrComplained = (row: AllocationsTableData) =>
    !(
      row.status === AllocationStatus.ACCEPTED ||
      row.status === AllocationStatus.REJECTED ||
      row.status === AllocationStatus.REQUIRES_CORRECTION
    );

  const rowsToBeAccepted = useMemo(
    () => rows?.filter((n) => canBeAcceptedOrComplained(n)) || [],
    [rows],
  );

  const handleSelectAllRowsClick = (e: ChangeEvent<HTMLInputElement>) => {
    let newSelected: number[] = [];
    if (e.target.checked) {
      newSelected =
        rows?.filter((n) => canBeAcceptedOrComplained(n)).map((n) => n.id) ||
        [];
      setEntriesToAccept(newSelected);
    }
    setSelectedRows(newSelected);
  };

  const handleChecked = (id: number) => (e: MouseEvent<unknown>) => {
    e.stopPropagation();
    const selectedRowsIndex = selectedRows.indexOf(id);
    let newSelected: number[] = [];

    if (selectedRowsIndex === -1) {
      newSelected = newSelected.concat(selectedRows, id);
    } else if (selectedRowsIndex === 0) {
      newSelected = newSelected.concat(selectedRows.slice(1));
    } else if (selectedRowsIndex === selectedRows.length - 1) {
      newSelected = newSelected.concat(selectedRows.slice(0, -1));
    } else if (selectedRowsIndex > 0) {
      newSelected = newSelected.concat(
        selectedRows.slice(0, selectedRowsIndex),
        selectedRows.slice(selectedRowsIndex + 1),
      );
    }

    setSelectedRows(newSelected);
    setEntriesToAccept(newSelected);
  };

  const handleAllocationDetailsOpen =
    (allocation: AllocationsTableData) => () => {
      setSelectedAllocation(data?.find((a) => allocation.id === a.id));
      setAllocationDetailsDialogOpen(true);
    };

  const handleAllocationDetailsClose = () => {
    setSelectedAllocation(undefined);
    setAllocationDetailsDialogOpen(false);
  };

  const handleRejectAllocationOpen = (id: number) => () => {
    setActionEntryId(id);
    setRejectAllocationDialogOpen(true);
  };

  const handleConfirmAllocationOpen = (ids: number[]) => () => {
    setEntriesToAccept(ids);
    setConfirmAllocationDialogOpen(true);
  };

  const handleConfirmAllocationClose = () => {
    setConfirmAllocationDialogOpen(false);
    setEntriesToAccept([]);
  };

  const handleComplaintAllocationOpen = (id: number) => () => {
    setActionEntryId(id);
    setComplaintAllocationDialogOpen(true);
  };

  const handleWithdrawCorrectionAllocationOpen = (id: number) => () => {
    setActionEntryId(id);
    setWithdrawCorrectionAllocationDialogOpen(true);
  };

  const handleActionSubmit = () => {
    refetchInvoiceData(invoice.id, isCorrective);
    getInvoiceAccountEntry(Number(invoice.id));
  };

  useEffect(() => {
    getInvoiceAccountEntry(Number(invoice.id));
  }, [getInvoiceAccountEntry, invoice.id]);

  const canRejectAllocation = (allocation: AccountEntry) =>
    // copied from frontend\src\main\webapp\frontend\src\modules\invoicesModule\invoices\allocation.js
    // this.canDelete method
    ((invoice.status !== InvoiceStatus.EXPORTED &&
      hasPrivilege(PRIVILEGES.Invoices.DELETE_ALLOCATION, user)) ||
      (allocation.status === AllocationStatus.PENDING &&
        isAllocationOwner(allocation, user, allowedReplacements))) &&
    allocation.status !== AllocationStatus.REJECTED &&
    !invoice.invoiceClass.own &&
    !invoice.exportInformation &&
    !isAllocationJobBilledOrCancelled(allocation);
  const showRejectButton = (allocation: AccountEntry) => {
    if (
      allocation.status !== AllocationStatus.PENDING &&
      allocation.status !== AllocationStatus.ACCEPTED
    ) {
      return false;
    }
    return canRejectAllocation(allocation);
  };

  const isJobOpen = (allocation: AccountEntry) =>
    allocation.job.status === JobStatus.IN_PROGRESS ||
    allocation.job.status === JobStatus.FINALIZED;

  const showCorrectAndAcceptButton = (allocation: AccountEntry) => {
    if (allocation.status !== AllocationStatus.PENDING) {
      return false;
    }

    if (!isAllocationOwner(allocation, user, allowedReplacements)) {
      if (!canReviewAllocation(user)) {
        return false;
      }
    }

    return !!invoice.publicationDate && isJobOpen(allocation);
  };

  const showWithdrawButton = (allocation: AccountEntry) => {
    if (allocation.status !== AllocationStatus.REQUIRES_CORRECTION) {
      return false;
    }
    if (!isAllocationOwner(allocation, user, allowedReplacements)) {
      if (!canReviewAllocation(user)) {
        return false;
      }
    }

    return !!invoice.publicationDate && isJobOpen(allocation);
  };

  return (
    <TableContainer component={Paper}>
      <AddAllocationDialog
        open={addAllocationDialogOpen}
        onSubmit={handleActionSubmit}
        onClose={() => setAddAllocationDialogOpen(false)}
        unallocatedAmount={Number(
          (
            invoice.totals.netValue - invoice.totals.netValueAllocationSum
          ).toFixed(2),
        )}
        invoiceCode={invoice.code}
        invoiceClass={invoice.invoiceClass}
        invoiceId={invoice.id}
        currencyCode={invoice.currencyCode}
        accountEntriesLoading={accountEntriesLoading}
      />
      <AllocationDetails
        open={allocationDetailsDialogOpen}
        allocationData={selectedAllocation as AccountEntry}
        invoice={invoice}
        unallocatedAmount={Number(
          (
            invoice.totals.netValue - invoice.totals.netValueAllocationSum
          ).toFixed(2),
        )}
        accountEntriesLoading={accountEntriesLoading}
        onClose={handleAllocationDetailsClose}
        onSubmit={handleActionSubmit}
      />
      <RejectAllocationDialog
        open={rejectAllocationDialogOpen}
        onClose={() => setRejectAllocationDialogOpen(false)}
        onSubmit={handleActionSubmit}
        accountEntryId={actionEntryId}
        accountEntriesLoading={accountEntriesLoading}
      />
      <ConfirmAllocationDialog
        open={confirmAllocationDialogOpen}
        onClose={handleConfirmAllocationClose}
        onSubmit={handleActionSubmit}
        accountEntryIds={entriesToAccept}
        accountEntriesLoading={accountEntriesLoading}
      />
      <ComplaintAllocationDialog
        open={complaintAllocationDialogOpen}
        onClose={() => setComplaintAllocationDialogOpen(false)}
        onSubmit={handleActionSubmit}
        accountEntryId={actionEntryId}
        accountEntriesLoading={accountEntriesLoading}
      />
      <WithdrawCorrectionAllocationDialog
        open={withdrawCorrectionAllocationDialogOpen}
        onClose={() => setWithdrawCorrectionAllocationDialogOpen(false)}
        onSubmit={handleActionSubmit}
        accountEntryId={actionEntryId}
        accountEntriesLoading={accountEntriesLoading}
      />
      {rows && (
        <>
          <S.InvoiceNetValueContainer>
            {`${t('invoiceAmount', { ns: 'invoice' })}: ${numberWithCommas(
              invoice.totals.netValue,
            )} ${invoice.currencyCode.currency}`}
          </S.InvoiceNetValueContainer>
          <Divider />
          <Table aria-labelledby='tableTitle' size='medium'>
            <TableHead>
              <TableRow>
                <StyledTableHeadCell padding='checkbox'>
                  <Checkbox
                    color='primary'
                    indeterminate={
                      selectedRows.length > 0 &&
                      selectedRows.length < rowsToBeAccepted.length
                    }
                    checked={
                      rows.length > 0 &&
                      selectedRows.length !== 0 &&
                      selectedRows.length === rowsToBeAccepted.length
                    }
                    disabled={!rowsToBeAccepted.length}
                    onChange={handleSelectAllRowsClick}
                  />
                </StyledTableHeadCell>
                {allocationsHeadCells.map((cell) => (
                  <StyledTableHeadCell
                    key={cell.id}
                    align={cell.align || 'left'}
                    padding={'normal'}
                  >
                    {cell.label}
                  </StyledTableHeadCell>
                ))}
                <StyledTableHeadCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows?.map((row) => {
                const isItemSelected = isRowSelected(row.id);
                return (
                  <TableRow key={row.id} hover>
                    <TableCell padding='checkbox'>
                      <Checkbox
                        color='primary'
                        checked={isItemSelected}
                        onClick={handleChecked(row.id)}
                        disabled={!canBeAcceptedOrComplained(row)}
                      />
                    </TableCell>
                    {allocationsHeadCells.map((cell) => (
                      <StyledTableCell
                        key={cell.id}
                        align={cell.align || 'left'}
                        height='condensed'
                        onClick={handleAllocationDetailsOpen(row)}
                      >
                        <>
                          {cell.id === 'status'
                            ? allocationStatus[row[cell.id]]
                            : row[cell.id]}
                        </>
                      </StyledTableCell>
                    ))}
                    <TableCell padding='checkbox'>
                      <S.ActionsContainer>
                        <S.StyledIconButton
                          size='small'
                          color='inherit'
                          title={t('goToJob', { ns: 'invoice' })}
                          onClick={() => navigate(`/jobs/${row.jobId}`)}
                        >
                          <FaShare size={18} />
                        </S.StyledIconButton>
                        {canEditInvoice(
                          invoice,
                          user,
                          allowedReplacementsByCode,
                        ) && (
                          <>
                            {showRejectButton(row.allocationData) && (
                              <S.StyledIconButton
                                size='small'
                                color='inherit'
                                title={t('button.reject', { ns: 'misc' })}
                                onClick={handleRejectAllocationOpen(row.id)}
                              >
                                <MdDelete size={20} />
                              </S.StyledIconButton>
                            )}
                            {showCorrectAndAcceptButton(row.allocationData) && (
                              <S.StyledIconButton
                                size='small'
                                color='inherit'
                                title={t('accept', { ns: 'invoice' })}
                                onClick={handleConfirmAllocationOpen([row.id])}
                              >
                                <ImCheckmark size={18} />
                              </S.StyledIconButton>
                            )}
                            {showCorrectAndAcceptButton(row.allocationData) && (
                              <S.StyledIconButton
                                size='small'
                                color='inherit'
                                title={t('markAllocationForCorrection', {
                                  ns: 'invoice',
                                })}
                                onClick={handleComplaintAllocationOpen(row.id)}
                              >
                                <FaExclamation size={16} />
                              </S.StyledIconButton>
                            )}
                            {showWithdrawButton(row.allocationData) &&
                              row.status === 'REQUIRES_CORRECTION' && (
                                <S.StyledIconButton
                                  size='small'
                                  color='inherit'
                                  title={t('withdraw', { ns: 'invoice' })}
                                  onClick={handleWithdrawCorrectionAllocationOpen(
                                    row.id,
                                  )}
                                >
                                  <FaBan size={17} />
                                </S.StyledIconButton>
                              )}
                          </>
                        )}
                      </S.ActionsContainer>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <S.StyledTableFooterCell />
                <S.StyledTableFooterCell>
                  {t('allocationSum', { ns: 'invoice' })}
                </S.StyledTableFooterCell>
                <S.StyledTableFooterCell>
                  {numberWithCommas(invoice.totals.netValueAllocationSum) +
                    ' ' +
                    invoice.currencyCode.currency}
                </S.StyledTableFooterCell>
                <S.StyledTableFooterCell colSpan={2} />
                <S.StyledTableFooterCell colSpan={2}>
                  <S.ButtonsContainer>
                    {canReviewAllocation(user) && (
                      <StyledButton
                        $primary
                        disabled={!selectedRows.length}
                        onClick={handleConfirmAllocationOpen(entriesToAccept)}
                      >
                        {t('accept', { ns: 'invoice' })}
                      </StyledButton>
                    )}
                    {hasPrivilege(PRIVILEGES.Invoices.ADD_ALLOCATION, user) &&
                      !isInvoiceAllocationExported(invoice) && (
                        <StyledButton
                          $primary
                          title={t('button.create', { ns: 'misc' })}
                          onClick={() => setAddAllocationDialogOpen(true)}
                        >
                          {t('button.create', { ns: 'misc' })}
                        </StyledButton>
                      )}
                  </S.ButtonsContainer>
                </S.StyledTableFooterCell>
              </TableRow>
            </TableFooter>
          </Table>
        </>
      )}
    </TableContainer>
  );
};
